/**
 * StageBackdrop — Liquid glass container for the bottom-center conversation area.
 *
 * The video sits inside a translucent glass frame with internal padding,
 * showing the glass border and backdrop blur around the avatar video.
 * Rounded top corners, flush with viewport bottom.
 */

import { createGlassPanel } from '../glass'
import { springEnter, springExit, type SpringAnimation } from '../spring'
import { injectTokens, tokens } from '../tokens'
import { autoDetectAndApply } from '../themeDetector'

const STAGE_WIDTH = 520
const STAGE_HEIGHT = 340
const VIDEO_PADDING = 12

export class GlassStageBackdrop {
  readonly el: HTMLDivElement
  private contentArea: HTMLDivElement
  private currentAnim: SpringAnimation | null = null

  constructor() {
    injectTokens()

    this.el = createGlassPanel({
      radius: 'lg',
      elevation: 'elevated',
      className: 'hs-stage-backdrop',
    }) as HTMLDivElement

    Object.assign(this.el.style, {
      position: 'fixed',
      bottom: '0',
      left: '0',
      right: '0',
      margin: '0 auto',
      width: `${STAGE_WIDTH}px`,
      height: `${STAGE_HEIGHT}px`,
      zIndex: tokens.zOverlay,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      opacity: '0',
      padding: `${VIDEO_PADDING}px ${VIDEO_PADDING}px 0 ${VIDEO_PADDING}px`,
      // Round only top corners — bottom sits flush with viewport edge
      borderRadius: 'var(--hs-radius-lg) var(--hs-radius-lg) 0 0',
      borderBottom: 'none',
    })

    autoDetectAndApply(this.el)

    // Content area — video sits inside with rounded corners
    this.contentArea = document.createElement('div')
    Object.assign(this.contentArea.style, {
      flex: '1',
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 'var(--hs-radius-md) var(--hs-radius-md) 0 0',
    })
    this.el.appendChild(this.contentArea)
  }

  /** Append a child (Anam video element) into the stage content area. */
  appendContent(child: HTMLElement): void {
    Object.assign(child.style, {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
      borderRadius: 'inherit',
    })
    this.contentArea.appendChild(child)
  }

  /** Mount the stage to the DOM with spring animation. */
  show(): void {
    document.body.appendChild(this.el)
    this.currentAnim = springEnter(this.el, {
      preset: 'gentle',
      from: { opacity: 0, scale: 0.95, y: 24 },
    })
  }

  /** Spring-animated exit, removes from DOM. */
  hide(): void {
    this.currentAnim?.stop()
    this.currentAnim = springExit(this.el, {
      remove: true,
      preset: 'smooth',
      to: { opacity: 0, scale: 0.95, y: 16 },
    })
  }

  get contentElement(): HTMLDivElement {
    return this.contentArea
  }

  destroy(): void {
    this.currentAnim?.stop()
    this.el.remove()
  }
}
